// Pure filter-seeding + profile write-back logic for the customer-page Matches tab.
//
// PORTED from contexts/ListingsMatchContext.tsx (getNewStateSearchFromContactFields,
// saveSearchStateToPickedContact) — deliberately NOT imported from there: the god-context
// is page-level tech debt. Only the business rules live here, as pure functions.
//
// Ported behaviors (keep in sync with the server-side suggest_listings inference):
// - budget ±10% buffer around the contact budget (customerBudget / customerBudgetRent);
//   when the NEW range fields (customerBudgetMin / customerBudgetRentMin) are present the
//   explicit lower bound wins over the -10% heuristic.
// - rent-vs-sale from Hebrew customer types: renter == שוכר and NOT also קונה.
// - locations from interestedInLocations, falling back to the deprecated comma-separated
//   `neighborhood` free-text field.
// - rooms / floors ranges (floors snapped to the closest valid Floor value).
// - built area (squareMeterMin / squareMeterMax) EXACTLY as stored — no buffer, so the chip reads
//   what the realtor typed on the card and the profile write-back is a round trip.
// - boolean amenity filters from the contact's GenericPropertyDetails fields, minus the
//   keys hidden in the legacy UI (no reliable Yad2 data yet).
import _ from "lodash";
import {
  floors,
  genericPropertyDetailsKeys,
  getContactCustomerTypes
} from "../../../../../../@types/sharedSchema";
import type {
  Room,
  Floor,
  ParkingSpot,
  ListingListedFor,
  RealEstateContact,
  GenericPropertyDetails,
  ProcessedListingAddress
} from "../../../../../../@types/sharedSchema";
import { toPersistedLocations } from "utils/locations/locationGroups";
import type { DateRangeFilterValue } from "components/_dashboard/common/DateRangeFilter";

export const BUDGET_BUFFER_PERCENTAGE = 0.1;

// Ported from ListingsMatchContext.hiddenBooleanPropertiesDetailsKeys — booleans we
// don't offer as filters because the scraped data can't answer them reliably yet.
//
// ⚠️ ממ״ק and מקלט CAME BACK 2026-09-22. Hiding them meant a contact who had ticked "ממ״ד or
// מקלט" on their card seeded only `hasProtectedSpace` here, so the tab hard-filtered on a ממ״ד —
// the one reading the customer had explicitly said they did not need. They are still sparse in
// the scraped data, which is exactly why they belong in the OR group rather than out of the UI:
// `buildMatchSearchRequest` sends the group and the server ORs it (`amenities && …`), so ticking
// one can only ever ADD listings. `hasHousingUnit` / `hasGarden` stay hidden — those ARE plain
// ANDed filters, and the data cannot answer them.
export const HIDDEN_BOOLEAN_FILTER_KEYS: ReadonlyArray<string> = ["hasHousingUnit", "hasGarden"];

export const VISIBLE_BOOLEAN_FILTER_KEYS = genericPropertyDetailsKeys.filter(
  (key) => !HIDDEN_BOOLEAN_FILTER_KEYS.includes(key)
);

/**
 * The search state of the Matches tab (the salvage-worthy subset of SearchState).
 *
 * Two tiers, and the split matters:
 *
 * - **Profile-mappable** (everything down to `booleanProps`) — these are seeded from the
 *   contact's saved search profile by `buildFiltersFromContact` and can be written BACK
 *   to it by `buildContactProfileUpdates`. `comparableFilters` covers exactly this tier,
 *   which is what makes the "update the contact's profile" nudge honest.
 * - **Advanced** (everything below) — reachable only through the full filters drawer
 *   (`MatchesFiltersDrawer`). The contact profile has no field for any of them, so they
 *   are NEVER seeded and never written back; they are pure per-search refinements.
 *   `comparableSearchFilters` is the tier that includes them — use it for the search
 *   identity key and for "do the filters differ from the contact's defaults".
 *
 * Every advanced field below is actually applied by `buildMatchSearchRequest` /
 * `searchMatchListings`. Do not add one here without adding its request field there —
 * a control that silently does nothing is worse than no control.
 */
export type MatchFilters = {
  listedFor: ListingListedFor;
  locations: ProcessedListingAddress[];
  minPrice?: number;
  maxPrice?: number;
  minRooms?: Room;
  maxRooms?: Room;
  minFloors?: Floor;
  maxFloors?: Floor;
  /**
   * Built area (שטח בנוי) in m² — free numbers, an open bound when absent, never 0.
   * PROFILE-MAPPABLE: seeded from `squareMeterMin` / `squareMeterMax` and written back to them.
   */
  minSquareMeter?: number;
  maxSquareMeter?: number;
  propertyTypes: string[];
  booleanProps: Partial<GenericPropertyDetails>;

  // ── advanced (never seeded from / written back to the profile) ──
  /**
   * Whether the budget also admits listings with NO price (Yad2 publishes those as
   * price 0 — "מחיר לא צוין"). Off by default, and meaningless without a budget: it
   * widens the price filter rather than being one of its own.
   *
   * Edited in the price chip rather than the drawer, but it belongs to the ADVANCED
   * tier all the same: the contact profile has no field for it, so it must not reach
   * `comparableFilters` — a divergence there raises a "update the search profile?"
   * nudge that `buildContactProfileUpdates` then has nothing to write.
   */
  includeListingsWithoutPrice?: boolean;
  minParkingSpots?: ParkingSpot;
  maxParkingSpots?: ParkingSpot;
  adTypes?: string[];
  agencyNames?: string[];
  sellerNames?: string[];
  isAssetExclusive?: boolean;
  onlyPriceChanged?: boolean;
  publishedDateRange?: DateRangeFilterValue;
  /**
   * Hide third-party listings not refreshed in STALE_LISTING_DAYS. `undefined` means
   * ON — the discovery surface has always age-hidden stale yad2/madlan results, and
   * the drawer merely makes that default visible and switchable.
   */
  hidePotentiallyRemovedListings?: boolean;
};

/**
 * Stored contact values are not trustworthy numbers: imports and older writers left
 * string values ("3") and empty strings on the numeric fields (repaired 2026-08-19,
 * but any not-yet-repaired or newly poisoned doc must not break this surface again).
 * A numeric string is coerced; anything else — including "" — is treated as absent.
 * Without this, "" reached getClosestValidFloor, coerced to 0 in the arithmetic, and
 * seeded a forced 0-0 floor filter; string rooms rode into the search request and
 * failed the endpoint's zod z.number() as a silent 400 → zero matches.
 */
export const asFiniteNumber = (value: unknown): number | undefined => {
  if (typeof value === "number") return isFinite(value) ? value : undefined;
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value.trim().replace(/,/g, ""));
    return isFinite(parsed) ? parsed : undefined;
  }
  return undefined;
};

/**
 * A stored SIZE bound — a positive number or nothing. Unlike a budget floor, 0 is never a real
 * bound here ("at least 0 m²" filters nothing), and the dialog's number box can leave one behind.
 */
export const asPositiveNumber = (value: unknown): number | undefined => {
  const parsed = asFiniteNumber(value);
  return parsed !== undefined && parsed > 0 ? parsed : undefined;
};

// Ported as-is from ListingsMatchContext.getClosestValidFloor (+ type hardening above).
export const getClosestValidFloor = (rawValue: number | null | undefined): Floor | undefined => {
  const value = asFiniteNumber(rawValue);
  if (value === undefined) return undefined;
  if ((floors as readonly number[]).includes(value)) return value as Floor;
  return (floors as readonly number[]).reduce((prev, curr) =>
    Math.abs(curr - value) < Math.abs(prev - value) ? curr : prev
  ) as Floor;
};

/** Rent search only when the contact is a tenant and NOT also a buyer (legacy + MCP parity). */
export function isRenterContact(contact: RealEstateContact | null | undefined): boolean {
  const types = getContactCustomerTypes(contact);
  return types.includes("שוכר") && !types.includes("קונה");
}

const undefinedIfNotPositive = (value: number | null | undefined): number | undefined =>
  typeof value === "number" && isFinite(value) && value > 0 ? value : undefined;

/**
 * The same test for a stored LOWER bound, which — unlike a budget — may legitimately
 * be ZERO: "0 to 1.5M" is a hard ceiling with no floor, and is a different instruction
 * from a lone 1.5M budget (which the branch below spreads ±10% around). Reusing
 * `undefinedIfNotPositive` here silently turned a 0 floor back into that ±10% guess,
 * so the tab seeded 1.35M–1.65M for a contact who had asked for everything under 1.5M.
 *
 * A NEGATIVE floor is still rejected: it can only be corrupt data, and it would widen
 * the search rather than narrow it.
 */
const undefinedIfNegative = (value: number | null | undefined): number | undefined =>
  typeof value === "number" && isFinite(value) && value >= 0 ? value : undefined;

/**
 * Seed the tab's filters from the contact's stored search profile.
 *
 * `listedForOverride` seeds the budget (and everything else) FOR that transaction
 * type instead of the contact-inferred one — used when the realtor flips the
 * למכירה/להשכרה toggle so the price bounds re-derive from the matching budget
 * field pair (a sale-scale budget on a rent search guarantees zero hits).
 */
export function buildFiltersFromContact(
  contact: RealEstateContact | null | undefined,
  listedForOverride?: ListingListedFor
): MatchFilters {
  const filters: MatchFilters = {
    listedFor: listedForOverride ?? "sale",
    locations: [],
    propertyTypes: [],
    booleanProps: {}
  };
  if (!contact) return filters;

  const isRenter = isRenterContact(contact);
  filters.listedFor = listedForOverride ?? (isRenter ? "rent" : "sale");

  // Locations: interestedInLocations wins; deprecated `neighborhood` (comma separated
  // free text) is the legacy fallback, mapped to neighborhood-only address filters.
  if (contact.interestedInLocations && contact.interestedInLocations.length > 0) {
    filters.locations = contact.interestedInLocations;
  } else if (contact.neighborhood) {
    filters.locations = contact.neighborhood
      .split(",")
      .map((n) => n.trim())
      .filter(Boolean)
      .map((n) => ({ neighborhood: n } as ProcessedListingAddress));
  }

  if (contact.interestedInPropertyTypes && contact.interestedInPropertyTypes.length > 0) {
    filters.propertyTypes = contact.interestedInPropertyTypes;
  }

  filters.minRooms = asFiniteNumber(contact.roomsMin) as Room | undefined;
  filters.maxRooms = asFiniteNumber(contact.roomsMax) as Room | undefined;
  filters.minFloors = getClosestValidFloor(contact.floorsMin);
  filters.maxFloors = getClosestValidFloor(contact.floorsMax);
  filters.minSquareMeter = asPositiveNumber(contact.squareMeterMin);
  filters.maxSquareMeter = asPositiveNumber(contact.squareMeterMax);

  // Budget: the stored budget is semantically the MAX. Legacy applied ±10% around it.
  // Two refinements over the legacy heuristic:
  // - When the explicit range fields (customerBudgetMin / customerBudgetRentMin) are
  //   present, the stored range [min, max] is used EXACTLY (no ±10% inflation) — this
  //   makes the seed ↔ profile-write-back round trip a fixed point (accepting the
  //   nudge no longer visibly inflates the max the realtor just typed by +10%).
  // - Budget is read for filters.listedFor's field pair. The legacy
  //   customerBudgetRent||customerBudget fallback is kept only for inferred renter
  //   contacts (their budget was historically stored in customerBudget); a buyer
  //   flipped to rent gets no sale-budget bleed-through.
  const useRentBudget = filters.listedFor === "rent";
  const budgetMax = undefinedIfNotPositive(
    useRentBudget
      ? contact.customerBudgetRent || (isRenter ? contact.customerBudget : undefined)
      : contact.customerBudget
  );
  const budgetMin = undefinedIfNegative(
    useRentBudget ? contact.customerBudgetRentMin : contact.customerBudgetMin
  );
  if (budgetMax) {
    filters.minPrice = budgetMin ?? Math.round(budgetMax * (1 - BUDGET_BUFFER_PERCENTAGE));
    filters.maxPrice =
      budgetMin !== undefined ? budgetMax : Math.round(budgetMax * (1 + BUDGET_BUFFER_PERCENTAGE));
    // A floor of EXACTLY 0 is the realtor saying "no floor — everything under the
    // ceiling", so the unpriced listings belong in that answer: they are the ones
    // whose price is unknown rather than known-to-be-outside, and a search with no
    // bottom is the one search that cannot be excluding them on price. Yad2 publishes
    // ~26k of them (price 0, "מחיר לא צוין").
    //
    // Only an EXPLICIT 0 does this. The ±10% heuristic above can never produce one
    // (0.9 × a positive budget is positive), so this cannot fire for a contact who
    // merely has a lone budget — and it stays a seeded DEFAULT: unticking it in the
    // chip row is a divergence like any other, which `comparableSearchFilters` sees
    // and the reset-to-contact affordance restores.
    if (budgetMin === 0) filters.includeListingsWithoutPrice = true;
  }

  // Boolean amenities the contact asked for (true only), minus the hidden keys.
  const amenities = _.pickBy(
    _.pick(contact, VISIBLE_BOOLEAN_FILTER_KEYS),
    (v) => v === true
  ) as Partial<GenericPropertyDetails>;
  filters.booleanProps = amenities;

  return filters;
}

// Normalized, order-insensitive snapshot of the PROFILE-MAPPABLE tier only.
//
// Scope is deliberate: this is what the profile nudge and the profile write-back
// compare on. An advanced (drawer-only) filter has no contact field to diverge from,
// so including it here would raise a "your filters differ from the profile — update
// it?" prompt that buildContactProfileUpdates then has nothing to write for.
// For the search identity key and the reset affordance use comparableSearchFilters.
export function comparableFilters(filters: MatchFilters): Record<string, unknown> {
  return {
    listedFor: filters.listedFor,
    locations: [...(filters.locations ?? [])]
      .map((l) => `${l.city ?? ""}|${l.neighborhood ?? ""}|${l.street ?? ""}`)
      .sort(),
    minPrice: filters.minPrice ?? null,
    maxPrice: filters.maxPrice ?? null,
    minRooms: filters.minRooms ?? null,
    maxRooms: filters.maxRooms ?? null,
    minFloors: filters.minFloors ?? null,
    maxFloors: filters.maxFloors ?? null,
    minSquareMeter: filters.minSquareMeter ?? null,
    maxSquareMeter: filters.maxSquareMeter ?? null,
    propertyTypes: [...(filters.propertyTypes ?? [])].sort(),
    booleanProps: Object.keys(_.pickBy(filters.booleanProps ?? {}, Boolean)).sort()
  };
}

export function areFiltersEqual(a: MatchFilters, b: MatchFilters): boolean {
  return _.isEqual(comparableFilters(a), comparableFilters(b));
}

/**
 * Normalized snapshot of ALL filters — the profile-mappable tier plus the advanced
 * drawer-only ones. This is the search's identity: two filter objects with the same
 * snapshot produce the same server result set, so it is what `useMatchesSearch` keys
 * its pass on and what the "reset to the contact's filters" affordance compares.
 *
 * Normalization notes:
 * - `hidePotentiallyRemovedListings` collapses undefined → true (its effective default),
 *   so merely opening the drawer and pressing search can't look like a filter change.
 * - `publishedDateRange` collapses the "all" preset → null for the same reason, and
 *   reduces Dates to epoch millis so the key is stable across re-renders.
 */
export function comparableSearchFilters(filters: MatchFilters): Record<string, unknown> {
  const range = filters.publishedDateRange;
  return {
    ...comparableFilters(filters),
    includeListingsWithoutPrice: filters.includeListingsWithoutPrice === true,
    minParkingSpots: filters.minParkingSpots ?? null,
    maxParkingSpots: filters.maxParkingSpots ?? null,
    adTypes: [...(filters.adTypes ?? [])].sort(),
    agencyNames: [...(filters.agencyNames ?? [])].sort(),
    sellerNames: [...(filters.sellerNames ?? [])].sort(),
    isAssetExclusive: filters.isAssetExclusive ?? false,
    onlyPriceChanged: filters.onlyPriceChanged ?? false,
    hidePotentiallyRemovedListings: filters.hidePotentiallyRemovedListings !== false,
    publishedDateRange:
      !range || range.preset === "all"
        ? null
        : [
            range.preset,
            range.customFrom ? range.customFrom.getTime() : null,
            range.customTo ? range.customTo.getTime() : null
          ]
  };
}

/** Full-tier equality (profile-mappable + advanced). See comparableSearchFilters. */
export function areSearchFiltersEqual(a: MatchFilters, b: MatchFilters): boolean {
  return _.isEqual(comparableSearchFilters(a), comparableSearchFilters(b));
}

/**
 * True when the active filters still mirror what the contact's profile would seed
 * FOR THE ACTIVE listedFor. Comparing against the seed for filters.listedFor (not
 * the contact-inferred type) means a pure למכירה/להשכרה flip — an exploration
 * toggle, not a profile change — never counts as divergence, so the profile-update
 * nudge can't offer an "update" that buildContactProfileUpdates has no write path
 * for (accepting used to silently revert the flip).
 */
export function filtersMatchContactProfile(
  filters: MatchFilters,
  contact: RealEstateContact
): boolean {
  return areFiltersEqual(filters, buildFiltersFromContact(contact, filters.listedFor));
}

/** The price bounds buildFiltersFromContact would seed for the given transaction type. */
export function seedBudgetForListedFor(
  contact: RealEstateContact,
  listedFor: ListingListedFor
): Pick<MatchFilters, "minPrice" | "maxPrice"> {
  const seeded = buildFiltersFromContact(contact, listedFor);
  return { minPrice: seeded.minPrice, maxPrice: seeded.maxPrice };
}

/**
 * Contact-profile updates for the "עדכן פרופיל חיפוש" suggestion.
 *
 * Port of the legacy saveSearchStateToPickedContact write-back, upgraded in two ways:
 * - Only DIVERGENT filter groups are written (legacy always rewrote everything, which —
 *   with the ±10% seeding buffer — silently inflated an untouched budget on every save).
 * - Budget writes go to the proper rent/sale field pair and use the new range fields
 *   (customerBudgetMin / customerBudgetRentMin) instead of collapsing to a midpoint.
 *
 * Returns null when nothing diverges. All writes must go through AppContext.updateContact.
 */
export function buildContactProfileUpdates(
  filters: MatchFilters,
  contact: RealEstateContact
): Partial<RealEstateContact> | null {
  // Divergence is measured against the seed for the ACTIVE listedFor, so budget
  // edits made under a flipped toggle are compared to (and written into) the field
  // pair that seeding reads back for that type.
  const seeded = buildFiltersFromContact(contact, filters.listedFor);
  const updates: Partial<RealEstateContact> = {};

  const budgetChanged =
    filters.minPrice !== seeded.minPrice || filters.maxPrice !== seeded.maxPrice;
  if (budgetChanged) {
    const max = filters.maxPrice ?? filters.minPrice ?? null;
    // `!= null`, not truthiness: a floor of 0 ("everything under the max") is a real
    // bound and has to reach the document, or accepting the nudge writes null over it
    // and the profile silently reverts to a lone budget — which the seed above then
    // spreads ±10% around, moving the ceiling the realtor just set.
    const min = filters.minPrice != null && filters.maxPrice != null ? filters.minPrice : null;
    if (filters.listedFor === "rent") {
      updates.customerBudgetRent = max as number;
      updates.customerBudgetRentMin = min as number;
    } else {
      updates.customerBudget = max as number;
      updates.customerBudgetMin = min as number;
    }
  }

  if (filters.minRooms !== seeded.minRooms || filters.maxRooms !== seeded.maxRooms) {
    updates.roomsMin = filters.minRooms ?? null;
    updates.roomsMax = filters.maxRooms ?? null;
  }

  if (filters.minFloors !== seeded.minFloors || filters.maxFloors !== seeded.maxFloors) {
    updates.floorsMin = filters.minFloors ?? null;
    updates.floorsMax = filters.maxFloors ?? null;
  }

  if (
    filters.minSquareMeter !== seeded.minSquareMeter ||
    filters.maxSquareMeter !== seeded.maxSquareMeter
  ) {
    updates.squareMeterMin = filters.minSquareMeter ?? null;
    updates.squareMeterMax = filters.maxSquareMeter ?? null;
  }

  const locationsChanged = !_.isEqual(
    comparableFilters(filters).locations,
    comparableFilters(seeded).locations
  );
  if (locationsChanged) {
    // Without the location-group stamp: provenance belongs to a live selection,
    // not to the contact, and the server's `asInterestedInLocations` drops it on
    // the way in regardless.
    updates.interestedInLocations = toPersistedLocations(filters.locations ?? []);
    // The deprecated free-text `neighborhood` mirror is NO LONGER WRITTEN here.
    //
    // Contact edits now go through `POST /api/v1/contacts/update`, whose whitelist
    // deliberately refuses the legacy mirrors — so writing it made this tab's "עדכן את
    // פרופיל החיפוש" nudge a guaranteed 400 the moment any location changed. This path
    // was also the last writer left maintaining it; every other surface stopped years
    // ago, so keeping it here only made this one tab's copy disagree with the rest.
    //
    // Nothing regresses on the READ side: `buildFiltersFromContact` above still falls
    // back to `contact.neighborhood` when a legacy contact has no `interestedInLocations`
    // at all, which is the only case the mirror was ever consulted for.
  }

  if (!_.isEqual([...(filters.propertyTypes ?? [])].sort(), [...(seeded.propertyTypes ?? [])].sort())) {
    updates.interestedInPropertyTypes = filters.propertyTypes ?? [];
  }

  const amenitiesNow = _.pickBy(filters.booleanProps ?? {}, Boolean);
  const amenitiesSeeded = _.pickBy(seeded.booleanProps ?? {}, Boolean);
  if (!_.isEqual(Object.keys(amenitiesNow).sort(), Object.keys(amenitiesSeeded).sort())) {
    for (const key of VISIBLE_BOOLEAN_FILTER_KEYS) {
      const now = !!(amenitiesNow as Record<string, boolean>)[key];
      const before = !!(amenitiesSeeded as Record<string, boolean>)[key];
      if (now !== before) (updates as Record<string, boolean>)[key] = now;
    }
  }

  return Object.keys(updates).length > 0 ? updates : null;
}
